import { ButtonHTMLAttributes, ReactNode } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { ArrowForwardIcon, Css, ITheme, StyledButton } from '../../../ui';

export interface IButtonBaseProps {
	buttonType?: 'primary' | 'secondary';
	icon?: ReactNode;
	iconPosition?: 'left' | 'right';
	hideIcon?: boolean;
}

interface IButtonProps
	extends Omit<
			ButtonHTMLAttributes<HTMLButtonElement>,
			'dangerouslySetInnerHTML'
		>,
		IButtonBaseProps {
	ariaLabel?: string;
	hasLabel?: boolean;
}

export default function Button({
	buttonType = 'primary',
	icon,
	iconPosition = 'right',
	disabled,
	ariaLabel,
	hideIcon,
	children,
	...props
}: IButtonProps) {
	return (
		<ButtonStyled
			secondary={buttonType === 'secondary'}
			disabled={disabled}
			aria-disabled={disabled}
			aria-label={ariaLabel}
			tabIndex={disabled ? -1 : 0}
			iconPosition={iconPosition}
			hasLabel={!!children}
			buttonType={buttonType}
			{...props}
		>
			{children && <ButtonLabel>{children}</ButtonLabel>}
			{icon || (!hideIcon && <ArrowForwardIcon aria-hidden />)}
		</ButtonStyled>
	);
}

const ButtonStyled = styled(StyledButton)<IButtonProps>`
	display: flex;
	flex-direction: ${({ iconPosition }) =>
		iconPosition === 'left' ? 'row-reverse' : 'row'};
	align-items: center;
	justify-content: center;

	${({ hasLabel }) =>
		!hasLabel &&
		css`
			padding: ${Css.pxToRem(12)};
			gap: 0;
		`}

	svg {
		path {
			fill: currentColor;
		}
	}

	&:disabled {
		svg {
			path {
				fill: currentColor;
			}
		}
	}
`;

export const ButtonLabel = styled.div<{}, ITheme>`
	font-family: ${({ theme }) => theme.typography.body.fontFamily};
	font-size: ${Css.pxToRem(16)};
	letter-spacing: 0.02em;
	line-height: normal;
	display: inline-block;
	min-width: fit-content;
`;
